// src/components/Auth/CreatePassword.tsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const CreatePassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('key');

  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [isValidToken, setIsValidToken] = useState(false);
  const [memberInfo, setMemberInfo] = useState<any>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Password strength requirements (matching PHP PasswordPolicy)
  const passwordRequirements = [
    { test: (p: string) => p.length >= 12, label: 'At least 12 characters' },
    { test: (p: string) => /[A-Z]/.test(p), label: 'One uppercase letter' },
    { test: (p: string) => /[a-z]/.test(p), label: 'One lowercase letter' },
    { test: (p: string) => /[0-9]/.test(p), label: 'One number' },
    { test: (p: string) => /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(p), label: 'One special character' },
  ];
  
  // Add password match requirement (only show if confirm password has been typed)
  const allRequirements = [
    ...passwordRequirements,
    ...(confirmPassword.length > 0 ? [
      { test: (p: string) => p === confirmPassword && p.length > 0, label: 'Passwords match' }
    ] : [])
  ];

  useEffect(() => {
    validateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const validateToken = async () => {
    if (!token) {
      setError('Invalid or missing token');
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/auth/validate-token', { token, type: 'create_password' });
      if (response.data.success) {
        setIsValidToken(true);
        setMemberInfo(response.data.data);
      } else {
        setError('Invalid or expired token. Please contact your administrator.');
      }
    } catch (err: any) {
      setError('Invalid or expired token. Please contact your administrator.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Check all requirements
    const failedRequirements = passwordRequirements.filter(req => !req.test(password));
    if (failedRequirements.length > 0) {
      setError('Password does not meet all requirements');
      return;
    }

    setValidating(true);
    setError(null);

    try {
      const response = await api.post('/endpoints/create-password', {
        token,
        password,
      });

      if (response.data.success) {
        setSuccess(true);
        const userData = response.data.data;

        // Redirect to phone verification after 2 seconds
        setTimeout(() => {
          navigate('/verify-signup-phone', {
            state: {
              userId: userData.user_id,
              email: userData.email,
              firstName: userData.first_name,
              lastName: userData.last_name
            }
          });
        }, 2000);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to create password';
      
      // Check if the error is about invalid/expired token
      if (err.response?.status === 400 && 
          (errorMessage.toLowerCase().includes('invalid') || 
           errorMessage.toLowerCase().includes('expired') ||
           errorMessage.toLowerCase().includes('token'))) {
        // Mark token as invalid to show the friendly expired message
        setIsValidToken(false);
      } else {
        setError(errorMessage);
      }
    } finally {
      setValidating(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f5f5f5"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isValidToken) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f5f5f5"
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 500, width: '100%', mx: 2 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
            Link Expired or Invalid
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body1" gutterBottom>
              This password creation link has expired or has already been used.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              For security reasons, password creation links are only valid for one use and expire after 7 days.
            </Typography>
          </Alert>

          <Typography variant="body1" sx={{ mb: 2 }}>
            Please contact your administrator to receive a new welcome email with a fresh password creation link.
          </Typography>

          <Button
            fullWidth
            variant="contained"
            onClick={() => navigate('/login')}
            sx={{ mt: 2 }}
          >
            Go to Login Page
          </Button>
        </Paper>
      </Box>
    );
  }

  if (success) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f5f5f5"
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 500, width: '100%', mx: 2, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Password Created Successfully!
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Next step: Verify your phone number
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Redirecting to phone verification...
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="#f5f5f5"
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 500, width: '100%', mx: 2 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <LockIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h5" component="h1">
            Create Your Password
          </Typography>
        </Box>

        {memberInfo && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Welcome, <strong>{memberInfo.first_name} {memberInfo.last_name}</strong>!
            Please create a password for your account.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            label="New Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            type={showConfirmPassword ? 'text' : 'password'}
            label="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="normal"
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ mt: 2, mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Password Requirements:
            </Typography>
            {allRequirements.map((req, index) => (
              <Typography
                key={index}
                variant="body2"
                color={req.test(password) ? 'success.main' : 'text.secondary'}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                {req.test(password) ? '✓' : '○'} {req.label}
              </Typography>
            ))}
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={validating}
            sx={{ mt: 2 }}
          >
            {validating ? <CircularProgress size={24} /> : 'Create Password'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default CreatePassword;